import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { ArcadeFieldLabel, ArcadeInput, CurrencyInput } from '@cash-design-system/react'
import { CurrencyCode } from '@cash-web/protos/squareup/common/currency_codes.pb'
import {
  FormBlockerElementMoneyInputElement,
  FormBlockerElementMoneyInputElementDenominationOption,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { formatMoney, GenericMoneyInput, getCurrencyPrefix } from '@cash-web/shared-feature-common'
import { FieldErrorText, FormInput } from '@cash-web/shared-feature-form'

import { FormBlockerAdditionalElementProps } from '../FormBlockerElement/FormBlockerElement'

export type MoneyInputElementProps = FormBlockerAdditionalElementProps & FormBlockerElementMoneyInputElement

const ArcadeMoneyInput = ({
  currencyCode = CurrencyCode.USD,
  ...props
}: React.ComponentProps<typeof ArcadeInput> & { currencyCode?: CurrencyCode }) => {
  return <ArcadeInput {...props} />
}

export const MoneyInputElement = ({
  elementId,
  formSectionIndex,
  shouldAutoFocus,

  // props below from FormBlockerElementMoneyInputElement
  label_text,
  disable_decimal, // to be deprecated but may still be used
  currency_code,
  hint_text,
  is_optional,
  maximum_amount,
  minimum_amount,
  prefill_amount,
  denomination_option,
  fractional_values_only, // to be deprecated but may still be used
}: MoneyInputElementProps) => {
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const currencyCode = currency_code || CurrencyCode.USD
  const currencyPrefix = getCurrencyPrefix(currencyCode)

  const maximumAmount = maximum_amount?.amount
  const minimumAmount = minimum_amount?.amount
  const prefillAmount = prefill_amount?.amount
    ? formatMoney({
        amount: prefill_amount?.amount,
        currency_code: currencyCode,
      })
    : ''
  const [formattedValue, setFormattedValue] = useState(prefillAmount)
  const formFieldName = `${formSectionIndex}.money_input_result.amount.amount`

  const disableDecimal = denomination_option
    ? denomination_option === FormBlockerElementMoneyInputElementDenominationOption.DOLLARS ||
      denomination_option === FormBlockerElementMoneyInputElementDenominationOption.CENTS
    : disable_decimal ?? false

  const fractionalValuesOnly = denomination_option
    ? denomination_option === FormBlockerElementMoneyInputElementDenominationOption.CENTS
    : fractional_values_only ?? false

  const renderArcadeMoneyInput = () => (
    <GenericMoneyInput
      ariaLabel={label_text}
      currencyPrefix={currencyPrefix}
      customInput={ArcadeMoneyInput}
      currencyCode={currencyCode}
      data-testid={elementId}
      defaultValue={prefillAmount}
      decimalScale={disableDecimal ? 0 : undefined}
      forceDecimalPoint={!disableDecimal}
      formattedValue={formattedValue}
      fractionalValuesOnly={fractionalValuesOnly}
      isRequired={!is_optional}
      maximumAmount={maximumAmount}
      minimumAmount={minimumAmount}
      name={formFieldName}
      placeholder={hint_text}
      setFormattedValue={setFormattedValue}
      shouldAutoFocus={shouldAutoFocus}
    />
  )

  return (
    <>
      {isArcadeMigrationEnabled ? (
        label_text ? (
          <ArcadeFieldLabel label={label_text}>{renderArcadeMoneyInput()}</ArcadeFieldLabel>
        ) : (
          renderArcadeMoneyInput()
        )
      ) : (
        <GenericMoneyInput
          ariaLabel={t('Amount in {CURRENCY}', { context: 'The label for a money input', CURRENCY: currency_code })}
          currencyPrefix={currencyPrefix}
          customInput={CurrencyInput}
          data-testid={elementId}
          defaultValue={prefillAmount}
          forceDecimalPoint={!disable_decimal}
          decimalScale={disable_decimal ? 0 : undefined}
          formattedValue={formattedValue}
          isRequired={!is_optional}
          maximumAmount={maximumAmount}
          minimumAmount={minimumAmount}
          name={formFieldName}
          placeholder={hint_text}
          setFormattedValue={setFormattedValue}
          shouldAutoFocus={shouldAutoFocus}
          prefix={undefined} // for Mooncake, CurrencyInput handles prefix
        />
      )}
      <FormInput
        name={`${formSectionIndex}.money_input_result.amount.currency_code`}
        defaultValue={currency_code}
        type="hidden"
      />
      <FieldErrorText name={formFieldName} />
    </>
  )
}
